import {
  Drawer,
  Hidden,
  IconButton,
  List,
  ListItemIcon,
  ListItemText
} from "@material-ui/core";
import ListItem from "@material-ui/core/ListItem";
import { Close, Menu } from "@material-ui/icons";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import ResetIQLogo from "assets/img/Reset IQ - Powered By A-B.png";
import axios from "axios";
import cx from "classnames";
import Config from "config";
import { Chain } from "interfaces/chain";
import { Info } from "lucide-react";
import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { State } from "state";
import { fetchChainProjects } from "state/logged_user_chains";
import { getHeaders } from "../helpers/headers";
import { getItem, removeItem } from "../helpers/storage";
import { SeverityLevel, ToastService } from "../services/toastService";
import { CHAIN_ROLE, IMPERSONATIONTOKEN, IMPERSONATIONUSER } from "../state/auth";
import BreadcrumbsV2 from "./BreadcrumbsV2";
import Link from "./Link";
import Loader from "./Loader";
import Profile from "./Profile";
import ProfileDropdown from "./ProfileDropdown";
import ProjectSelectModal from "./ProjectSelectModal";

// import Link from "components/Link";

// interface ProfileProps {
//   name: string;
//   role: string;
//   onClick: () => void;
//   authUser: User;
// }

interface LayoutProps {
  currentRoute?: string;
  impersonateUser?: any;
}
const logoutIconStyle: React.CSSProperties = { /* ... */ color: "#E5B42F" };
const logOutIconStyle: React.CSSProperties = { padding: "12px 16px", justifyContent: "end" };

type HeaderProps = RouteComponentProps & LayoutProps;

const Header: React.FC<HeaderProps> = ({ currentRoute, history, impersonateUser }) => {
  // State for the Profile Dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mobileMenu, setMobileMenu] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChain, setSelectedChain] = useState<Chain | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const authUser = useSelector((state: State) => state.auth_user.content);
  const [switchUserModalState, setSwitchUserModalState] = useState({
    loading: false,
  });

  const {
    loading,
    content: chains,
    error,
    projectsLoading,
    projectsError,
    chainProjects,
  } = useSelector((state: State) => state.logged_user_chains);

  const handleProfileClick = () => {
    // setAnchorEl(event.currentTarget);
    setIsDropdownOpen(true);
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedChain(null);
  };

  const handleChainClick = (chain: Chain) => {
    setSelectedChain(chain);
    dispatch(fetchChainProjects(chain.id));
    setIsModalOpen(true);
  };

  const styles = {
    header: {
      width: "100%",
      backgroundColor: "#C7C7C7",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)", // shadow-sm
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0.75rem 1.5rem", // py-3 px-6
    },
    left: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem", // gap-3
    },
    title: {
      fontSize: "1.25rem", // text-xl
      fontWeight: 600, // font-semibold
      fontFamily: "sans-serif",
      margin: 0,
      color: "black",
    },
    right: {
      display: "flex",
      alignItems: "center",
      gap: "1.5rem", // gap-4
    },
    button: {
      backgroundColor: " rgba(255, 255, 255, 0.25)",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: "0.25rem 0.5rem",
      borderRadius: "0.375rem", // rounded-md
    },
    buttonText: {
      fontSize: "12px",
      color: " rgba(55, 54, 54, 1)",
    },
  };

  if (!authUser) {
    // Note: If you have a separate loading state, use it here.
    return <div>Loading Header...</div>;
  }

  const handleReturnToMyAccount = async () => {
    const impersonationToken = getItem(IMPERSONATIONTOKEN);
    setSwitchUserModalState((p) => ({ ...p, loading: true }));
    await axios
      .post(
        `${Config.API_SERVICE_URL}/api/userImpersonate/stop`,
        JSON.stringify({ impersonationToken }),
        getHeaders()
      )
      .then(() => {
        setSwitchUserModalState((p) => ({ ...p, loading: false }));
      })
      .catch((error) => {
        setSwitchUserModalState((p) => ({ ...p, loading: false }));
        const message =
          error?.response?.data?.message || "Failed to switch user. Please try again.";
        ToastService.getInstance().publishToast(
          { identifier: "error-operation", title: "Message", message },
          SeverityLevel.ERROR,
          { autoClose: 3000 }
        );
      })
      .finally(() => {
        setSwitchUserModalState((p) => ({ ...p, loading: false }));
        removeItem(IMPERSONATIONTOKEN);
        removeItem(IMPERSONATIONUSER);
        removeItem(CHAIN_ROLE)
        window.location.pathname === "/associated-chains"
          ? window.location.reload()
          : history.push("/associated-chains");
      });
  };

  const list = () => (
    <div
      style={{ width: "80vw", maxWidth: "350px" }}
      role="presentation"
      onKeyDown={() => setMobileMenu(false)}
    >
      {/* Close button at the top */}
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "0.5rem" }}>
        <IconButton onClick={() => setMobileMenu(false)}>
          <Close />
        </IconButton>
      </div>

      <List>
        <ListItem
          button
          component="a"
          href="https://reset-iq.zendesk.com/hc/en-us/requests/new?"
          target="_blank"
          rel="noopener noreferrer"
        >
          <ListItemIcon>
            <Info />
          </ListItemIcon>
          <ListItemText primary="Request Support" />
        </ListItem>

        <ListItem button component={Link} to="/support" data-qa="header-link-support">
          <ListItemIcon>
            <Info />
          </ListItemIcon>
          <ListItemText primary="Support Documents" />
        </ListItem>
      </List>
    </div>
  );

  return (
    <Fragment>
      <header style={styles.header}>
        <div style={styles.left}>
          <Hidden mdUp>
            <IconButton
              color="inherit"
              aria-label="open menu"
              onClick={() => setMobileMenu(true)}
              edge="start"
              style={{ padding: "0", marginLeft: 0 }}
            >
              <Menu />
            </IconButton>
            <Drawer anchor={"left"} open={mobileMenu} onClose={() => setMobileMenu(false)}>
              {list()}
            </Drawer>
          </Hidden>

          <img
            src={ResetIQLogo}
            alt="logo"
            onClick={() => history.push("/associated-chains")}
            style={{
              height: "35px",     // adjust based on your header size
              width: "auto",
              objectFit: "contain",
              cursor: "pointer",
            }}
          />

          {/*<h1 style={styles.title} onClick={() => history.push("/associated-chains")}>BREW BOX</h1>*/}
          <Hidden smDown>
            <BreadcrumbsV2></BreadcrumbsV2>
          </Hidden>
        </div>
        <div style={styles.right}>
          <div style={{ display: "flex", gap: ".5rem" }}>
            <Hidden smDown>
              <section style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <a
                  href="https://reset-iq.zendesk.com/hc/en-us/requests/new?"
                  target="_blank"
                  rel="noreferrer noopener"
                  style={styles.button}
                >
                  <Info height={14} width={14} color={"#373636"} />
                  <span style={styles.buttonText}>Request Support</span>
                </a>
              </section>
              <section style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Link
                  to="/support"
                  className={cx({
                    "header-link-active": currentRoute === "/support",
                  })}
                  style={styles.button}
                  data-qa="header-link-support"
                >
                  <Info height={14} width={14} color={"#373636"} />
                  <span style={styles.buttonText}>Support Documents</span>
                </Link>
              </section>
            </Hidden>

            {impersonateUser && (
              <>
                <section style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontSize: "12px" }}>Operating as</span>
                  <div style={{ fontSize: "14px", fontWeight: 500 }}>
                    {impersonateUser?.first_name + " " + impersonateUser?.last_name}
                  </div>
                </section>
              </>
            )}
          </div>
          {impersonateUser ? (
            <div>
              <section style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <div
                  style={{ fontSize: "14px", fontWeight: 500, cursor: "pointer" }}
                  onClick={() => {
                    handleReturnToMyAccount();
                  }}
                >
                  Exit User Mode
                </div>
                <div>
                  <ListItem
                    button
                    onClick={() => {
                      handleReturnToMyAccount();
                      history.push("/logout");
                    }}
                    style={logOutIconStyle}
                  >
                    <ExitToAppIcon style={logoutIconStyle} />
                  </ListItem>
                </div>
                <div style={{ paddingTop: "2%" }}>
                  {switchUserModalState.loading && <Loader height={20} width={20} custom />}
                </div>
              </section>
            </div>
          ) : (
            <Profile
              name={authUser.first_name + " " + authUser.last_name}
              role={authUser.role}
              onClick={handleProfileClick}
            />
          )}
        </div>

        <ProfileDropdown
          open={isDropdownOpen}
          onClose={handleDropdownClose}
          // anchorEl is not strictly used by Dialog, but it's good practice
          // if you switch to Popover later
          anchorEl={anchorEl || null}
          onClick={(chain) => handleChainClick(chain)}
          name={authUser.first_name + " " + authUser.last_name}
          email={authUser.email}
          authUserRole={authUser.role}
        />

        <ProjectSelectModal
          open={isModalOpen}
          onClose={handleClose}
          projects={chainProjects}
          projectsLoading={projectsLoading}
          projectsError={projectsError}
          chainName={selectedChain?.name}
          onSelectProject={(project) => {
            handleClose();
          }}
        />
      </header>
      <Hidden mdUp>
        <BreadcrumbsV2></BreadcrumbsV2>
      </Hidden>
    </Fragment>
  );
};

export default withRouter(Header);
